import { useState, useRef, useEffect, useId } from 'react'
import { css, Theme, useTheme } from '@emotion/react'
import { RefObject } from 'react'

const containerStyle = (t: Theme) => css`
  display: flex;
  flex-flow: column;
  border-radius: 8px;
  border-width: 1px;
  background: ${t.c.ec.white};
  border: 1px solid ${t.c.ec.shallowGray};
  gap: 8px;
  padding: 10px 0px;
`

const menuContainerStyle = (menuWidth: number) => css`
  top: 100%;
  position: absolute;
  z-index: 900;
  width: ${menuWidth}px;
`

const subMenuContainerStyle = (subMenuWidth: number, t: Theme) => css`
  position: absolute;
  top: -11px;
  z-index: 900;
  width: ${subMenuWidth}px;
  box-shadow: 1px 1px 4px ${t.c.ec.mediumGray};
`

const absolteParentStyle = css`
  position: relative;
`
const buttonColorStyle = (t: Theme, isActive: boolean = false) => css`
  color: ${t.c.bc.text};
  background: ${t.c.ec.white};
  &:hover {
    background-color: ${t.c.ec.shallowGray};
    cursor: pointer;
  }

  ${isActive &&
  `
    background-color: ${t.c.ec.lightGray};
  `}
`

const buttonBaseStyle = (t: Theme) => css`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border-width: 1px;
  border: 1px solid ${t.c.ec.mediumGray};
  align-items: center;
  display: flex;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const menuItemStyle = (t: Theme) => css`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  height: 30px;
  column-gap: 8px;
  width: 100%;
`

const arrowIconStyle = css`
  width: 16px;
  height: 16px;
  fill: currentColor;
  margin-left: auto;
`

export type MenuItemType = {
  key: string
  value: React.ReactNode
  subMenuItems: SubMenuItemType[]
}
export type SubMenuItemType = {
  key: string
  value: React.ReactNode
  onClick: () => void | Promise<void>
}

export type Props = {
  triggerButtonText: React.ReactNode
  menuItems: MenuItemType[]
  style?: React.CSSProperties
  menuWidth: number
  subMenuWidth: number
}

export default function BaseMenu({
  triggerButtonText,
  menuItems,
  style,
  menuWidth,
  subMenuWidth,
}: Props) {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const parentRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null)
  const triggerButtonId = useId()
  // メニュー&ボタン外クリックで閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target) &&
        !triggerButtonRef.current?.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // メニューの位置を調整
  useEffect(() => {
    if (!isOpen || !menuRef.current || !triggerButtonRef.current) return

    const menu = menuRef.current
    const buttonRect = triggerButtonRef.current.getBoundingClientRect()

    // 画面右端に収まらない場合は right 寄せにする
    const menuWidth = menu.offsetWidth
    if (buttonRect.right + menuWidth > window.innerWidth) {
      menu.style.left = 'auto'
      menu.style.right = '0px'
    } else {
      menu.style.left = '0px'
      menu.style.right = 'auto'
    }
  }, [isOpen])

  return (
    <div css={absolteParentStyle} ref={parentRef}>
      <button
        type="button"
        style={style}
        onClick={() => setIsOpen(!isOpen)}
        css={[buttonBaseStyle(theme), buttonColorStyle(theme, isOpen)]}
        ref={triggerButtonRef}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        id={`${triggerButtonId}`}
      >
        {/* トリガーボタン */}
        <span>{triggerButtonText}</span>
        {/* ドロップダウンメニュー */}
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          css={[menuContainerStyle(menuWidth), containerStyle(theme)]}
          aria-labelledby={`${triggerButtonId}`}
          role="menu"
          id={`${triggerButtonId}-menu`}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.key}
              parentRef={menuRef}
              subMenuItems={item.subMenuItems}
              subMenuWidth={subMenuWidth}
            >
              {item.value}
            </MenuItem>
          ))}
        </div>
      )}
    </div>
  )
}

export type SubMenuProps = {
  parentRef: RefObject<HTMLDivElement | null>
  children: React.ReactNode
  subMenuItems: SubMenuItemType[]
  subMenuWidth: number
}
export function MenuItem({
  parentRef,
  children,
  subMenuItems,
  subMenuWidth,
}: SubMenuProps) {
  const theme = useTheme()
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)
  const menuItemRef = useRef<HTMLButtonElement | null>(null)
  const subMenuRef = useRef<HTMLDivElement | null>(null)
  const subMenuItemRef = useRef<HTMLButtonElement | null>(null)
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const menuItemId = useId()

  // サブメニュー選択解除
  const handleClick = (key: string) => {
    setActiveKey(key)
  }

  // サブメニューの位置を調整
  useEffect(() => {
    if (!isSubMenuOpen || !subMenuRef.current || !parentRef.current) return
    const subMenu = subMenuRef.current
    const menuRect = parentRef.current.getBoundingClientRect()

    if (menuRect.right + subMenuWidth > window.innerWidth) {
      subMenu.style.left = `-${subMenuWidth}px`
    } else {
      subMenu.style.left = '100%'
    }
  }, [isSubMenuOpen])

  return (
    <div css={absolteParentStyle}>
      <button
        type="button"
        css={[menuItemStyle(theme), buttonColorStyle(theme, isSubMenuOpen)]}
        onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
        ref={menuItemRef}
        aria-expanded={isSubMenuOpen}
        aria-haspopup="menu"
        role="menuitem"
        id={`${menuItemId}`}
      >
        {children}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 320 512"
          css={arrowIconStyle}
        >
          <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
        </svg>
      </button>
      {isSubMenuOpen && (
        <div
          ref={subMenuRef}
          css={[
            subMenuContainerStyle(subMenuWidth, theme),
            containerStyle(theme),
          ]}
          aria-labelledby={`${menuItemId}`}
          role="menu"
          id={`${menuItemId}-sub-menu`}
        >
          {subMenuItems.map((item) => (
            <button
              type="button"
              key={item.key}
              css={[
                menuItemStyle(theme),
                buttonColorStyle(theme, activeKey === item.key),
              ]}
              ref={subMenuItemRef}
              onClick={() => {
                handleClick(item.key)
                item.onClick()
              }}
              role="menuitem"
            >
              {item.value}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
